import { useState, useEffect } from 'react';
import api from '../api';
import { useLang } from '../i18n/LanguageContext';

function getStatusMeta(t) {
  return {
    upcoming:  { label: t('statusUpcoming'),  color: 'var(--cyan)',   bg: 'rgba(0,212,255,0.12)' },
    active:    { label: t('statusActive'),    color: 'var(--green)', bg: 'rgba(0,255,136,0.12)' },
    finished:  { label: t('statusFinished'),  color: 'var(--text2)', bg: 'rgba(255,255,255,0.04)' },
    cancelled: { label: t('statusCancelled'), color: 'var(--red)',   bg: 'rgba(255,59,92,0.12)' },
  };
}


export default function Tournaments() {
  const { t } = useLang();
  const [tournaments, setTournaments] = useState([]);
  const [selected, setSelected]       = useState(null);
  const [participants, setParticipants] = useState([]);
  const [labs, setLabs]               = useState([]);
  const [labId, setLabId]             = useState('');
  const [showForm, setShowForm]       = useState(false);
  const [form, setForm]               = useState({ name: '', game: '', description: '', start_at: '', end_at: '', max_players: 16, entry_fee: 0, prize_pool: 0 });
  const [guestName, setGuestName]     = useState('');
  const [loading, setLoading]         = useState(false);

  const STATUS_META = getStatusMeta(t);

  useEffect(() => {
    api.get('/owner/labs').then(r => {
      setLabs(r.data);
      if (r.data.length) setLabId(parseInt(localStorage.getItem('selected_lab_id')) || r.data[0].id);
    }).catch(() => {});
    loadTournaments();
  }, []);

  const loadTournaments = () => {
    api.get('/tournaments').then(r => setTournaments(r.data)).catch(() => {});
  };

  const loadParticipants = (id) => {
    api.get(`/tournaments/${id}/participants`).then(r => setParticipants(r.data)).catch(() => {});
  };

  const openDetail = (tour) => {
    setSelected(tour);
    loadParticipants(tour.id);
  };

  const createTournament = async () => {
    if (!form.name || !labId) return;
    setLoading(true);
    try {
      await api.post('/tournaments', { ...form, lab_id: labId });
      loadTournaments();
      setForm({ name: '', game: '', description: '', start_at: '', end_at: '', max_players: 16, entry_fee: 0, prize_pool: 0 });
      setShowForm(false);
    } catch (e) {
      alert(e.response?.data?.error || t('error'));
    } finally { setLoading(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/tournaments/${id}`, { status });
      loadTournaments();
      if (selected?.id === id) setSelected(prev => ({ ...prev, status }));
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const addParticipant = async () => {
    if (!selected) return;
    try {
      await api.post(`/tournaments/${selected.id}/participants`, { guest_name: guestName });
      setGuestName('');
      loadParticipants(selected.id);
      loadTournaments();
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const togglePaid = async (tourId, regId, paid) => {
    try {
      await api.patch(`/tournaments/${tourId}/participants/${regId}`, { paid: !paid });
      loadParticipants(tourId);
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  const removeParticipant = async (tourId, regId) => {
    try {
      await api.patch(`/tournaments/${tourId}/participants/${regId}`, { status: 'cancelled' });
      loadParticipants(tourId);
    } catch (e) { alert(e.response?.data?.error || t('error')); }
  };

  if (selected) {
    const sm = STATUS_META[selected.status] || STATUS_META.upcoming;
    const activeParticipants = participants.filter(p => p.status !== 'cancelled');
    const paidCount = activeParticipants.filter(p => p.paid).length;
    const totalFees = paidCount * parseFloat(selected.entry_fee || 0);

    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={() => setSelected(null)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', padding: '6px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>{t('tournamentBack')}</button>
          <h1 style={{ fontSize: 20, fontWeight: 700, flex: 1 }}>{selected.name}</h1>
          <span style={{ background: sm.bg, border: `1px solid ${sm.color}`, color: sm.color, padding: '4px 12px', borderRadius: 20, fontSize: 12 }}>{sm.label}</span>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 24 }}>
          {[
            { icon: '👥', value: `${activeParticipants.length}/${selected.max_players}`, label: t('participants'), color: 'var(--cyan)' },
            { icon: '💰', value: `${Number(selected.entry_fee || 0).toLocaleString()} ${t('currency')}`, label: t('entryFeeLabel'), color: 'var(--green)' },
            { icon: '🏆', value: `${Number(selected.prize_pool || 0).toLocaleString()} ${t('currency')}`, label: t('prizePool'), color: '#ffd700' },
            { icon: '💵', value: `${Number(totalFees).toLocaleString()} ${t('currency')}`, label: t('collectedFees'), color: 'var(--purple)' },
          ].map(k => (
            <div key={k.label} style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 12, padding: 16 }}>
              <div style={{ fontSize: 22, marginBottom: 6 }}>{k.icon}</div>
              <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 16, fontWeight: 700, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 3 }}>{k.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {selected.status === 'upcoming' && (
            <button onClick={() => updateStatus(selected.id, 'active')} style={{ background: 'rgba(0,255,136,0.12)', border: '1px solid var(--green)', color: 'var(--green)', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>{t('tournamentStart')}</button>
          )}
          {selected.status === 'active' && (
            <button onClick={() => updateStatus(selected.id, 'finished')} style={{ background: 'rgba(255,215,0,0.12)', border: '1px solid #ffd700', color: '#ffd700', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>{t('tournamentFinish')}</button>
          )}
          {['upcoming', 'active'].includes(selected.status) && (
            <button onClick={() => updateStatus(selected.id, 'cancelled')} style={{ background: 'rgba(255,59,92,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>{t('tournamentCancel')}</button>
          )}
        </div>

        {['upcoming', 'active'].includes(selected.status) && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
            <input value={guestName} onChange={e => setGuestName(e.target.value)} onKeyDown={e => e.key === 'Enter' && addParticipant()} placeholder={t('participantName')}
              style={{ flex: 1, background: 'var(--card2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 14px', borderRadius: 8, fontSize: 13 }} />
            <button onClick={addParticipant} style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)', color: 'var(--cyan)', padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>{t('addParticipant')}</button>
          </div>
        )}

        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, overflow: 'hidden' }}>
          <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border)', fontSize: 11, color: 'var(--text2)', letterSpacing: 2, textTransform: 'uppercase' }}>
            {t('participantsLabel')} ({activeParticipants.length})
          </div>
          {activeParticipants.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--text3)', fontSize: 13 }}>{t('noParticipants')}</div>
          ) : activeParticipants.map((p, i) => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'center', padding: '11px 20px', borderBottom: i < activeParticipants.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <span style={{ width: 28, fontSize: 13, color: 'var(--text3)', fontFamily: 'Orbitron, sans-serif' }}>#{i + 1}</span>
              <span style={{ flex: 1, fontSize: 13 }}>{p.user_name || p.guest_name || t('guestLabel')}</span>
              {p.user_phone && <span style={{ fontSize: 11, color: 'var(--text3)', marginRight: 16 }}>{p.user_phone}</span>}
              <button onClick={() => togglePaid(selected.id, p.id, p.paid)} style={{
                marginRight: 8, fontSize: 11, padding: '3px 10px', borderRadius: 6, cursor: 'pointer',
                background: p.paid ? 'rgba(0,255,136,0.1)' : 'rgba(255,165,0,0.1)',
                border: `1px solid ${p.paid ? 'var(--green)' : '#ffa500'}`,
                color: p.paid ? 'var(--green)' : '#ffa500',
              }}>{p.paid ? t('participantPaid') : t('participantUnpaid')}</button>
              <button onClick={() => removeParticipant(selected.id, p.id)} style={{ fontSize: 11, padding: '3px 8px', borderRadius: 6, cursor: 'pointer', background: 'rgba(255,59,92,0.1)', border: '1px solid var(--red)', color: 'var(--red)' }}>✕</button>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700 }}>{t('tournamentsTitle')}</h1>
          <div style={{ color: 'var(--text2)', fontSize: 13, marginTop: 4 }}>{t('tournamentsSubtitle')}</div>
        </div>
        <button onClick={() => setShowForm(!showForm)} style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid var(--cyan)', color: 'var(--cyan)', padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>{t('newTournament')}</button>
      </div>

      {showForm && (
        <div style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>{t('newTournamentForm')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12, marginBottom: 12 }}>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={t('tournamentName')}
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />
            <input value={form.game} onChange={e => setForm(f => ({ ...f, game: e.target.value }))} placeholder={t('tournamentGame')}
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />
            <input value={form.start_at} onChange={e => setForm(f => ({ ...f, start_at: e.target.value }))} type="datetime-local"
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />
            <input value={form.end_at} onChange={e => setForm(f => ({ ...f, end_at: e.target.value }))} type="datetime-local"
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />
            <input value={form.max_players} onChange={e => setForm(f => ({ ...f, max_players: e.target.value }))} type="number" placeholder={t('tournamentMaxPlayers')}
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />
            <input value={form.entry_fee} onChange={e => setForm(f => ({ ...f, entry_fee: e.target.value }))} type="number" placeholder={t('tournamentEntryFee')}
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />
            <input value={form.prize_pool} onChange={e => setForm(f => ({ ...f, prize_pool: e.target.value }))} type="number" placeholder={t('tournamentPrizePool')}
              style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }} />
            {false && labs.length > 1 && (
              <select value={labId} onChange={e => setLabId(e.target.value)} style={{ background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12 }}>
                {labs.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            )}
          </div>
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder={t('tournamentDescription')}
            rows={2} style={{ width: '100%', background: 'var(--bg2)', border: '1px solid var(--border)', color: 'var(--text)', padding: '8px 12px', borderRadius: 8, fontSize: 12, resize: 'vertical', marginBottom: 12 }} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={createTournament} disabled={loading} style={{ background: 'var(--cyan)', border: 'none', color: '#000', padding: '8px 20px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700 }}>{t('tournamentCreate')}</button>
            <button onClick={() => setShowForm(false)} style={{ background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 12 }}>{t('cancelShort')}</button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
        {tournaments.length === 0 ? (
          <div style={{ gridColumn: '1/-1', background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🏆</div>
            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>{t('noTournaments')}</div>
            <div style={{ fontSize: 12, color: 'var(--text2)' }}>{t('noTournamentsDesc')}</div>
          </div>
        ) : tournaments.map(tour => {
          const sm = STATUS_META[tour.status] || STATUS_META.upcoming;
          const pct = Math.round((tour.registered_count / tour.max_players) * 100);
          return (
            <div key={tour.id} onClick={() => openDetail(tour)} style={{ background: 'var(--card2)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, cursor: 'pointer', transition: 'border-color 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--cyan)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <span style={{ background: sm.bg, border: `1px solid ${sm.color}`, color: sm.color, padding: '2px 10px', borderRadius: 20, fontSize: 10 }}>{sm.label}</span>
                {tour.game && <span style={{ fontSize: 11, color: 'var(--text3)', background: 'rgba(255,255,255,0.04)', padding: '2px 8px', borderRadius: 6 }}>{tour.game}</span>}
              </div>

              <div style={{ fontFamily: 'Orbitron, sans-serif', fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{tour.name}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 12 }}>{tour.lab_name}</div>

              <div style={{ background: 'var(--border)', borderRadius: 4, height: 4, marginBottom: 8 }}>
                <div style={{ background: 'var(--cyan)', borderRadius: 4, height: '100%', width: `${pct}%`, transition: 'width 0.3s' }} />
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text2)', marginBottom: 10 }}>
                <span>👥 {tour.registered_count}/{tour.max_players}</span>
                {tour.start_at && <span>📅 {new Date(tour.start_at).toLocaleDateString('uz-UZ')}</span>}
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                {tour.entry_fee > 0 && <span style={{ color: 'var(--green)', fontWeight: 600 }}>{Number(tour.entry_fee).toLocaleString()} {t('currency')} {t('entryFeeEntry')}</span>}
                {tour.prize_pool > 0 && <span style={{ color: '#ffd700' }}>🏆 {Number(tour.prize_pool).toLocaleString()} {t('currency')}</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
